import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import {
  Phone,
  MessageCircle,
  Menu,
  X,
  Mail,
  MapPin,
  Clock,
  Calendar,
  GraduationCap,
  Award,
  CheckCircle2,
  Star,
  ArrowRight,
  Stethoscope,
  Microscope,
  Activity,
  Heart,
  Brain,
  ShieldCheck,
  Quote,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Users,
  ArrowUpRight,
  Youtube,
  Instagram,
  Facebook,
  Globe,
  Newspaper,
  PlayCircle,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PROFILE,
  CLINICS,
  SERVICES,
  TIMELINE,
  EDUCATION,
  MEMBERSHIPS,
  PUBLICATIONS,
  TESTIMONIALS,
  BLOG,
  GALLERY,
  PRESS,
  VIDEOS,
} from "@/data/site";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = { Stethoscope, Microscope, Activity, Heart, Brain, ShieldCheck };

const SOCIAL_LINKS = [
  { href: PROFILE.socials.youtube, label: "YouTube", Icon: Youtube, color: "hover:text-red-600" },
  { href: PROFILE.socials.instagram, label: "Instagram", Icon: Instagram, color: "hover:text-pink-600" },
  { href: PROFILE.socials.facebook, label: "Facebook", Icon: Facebook, color: "hover:text-blue-600" },
  { href: PROFILE.socials.google, label: "Google", Icon: Globe, color: "hover:text-emerald-600" },
];

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

// ------------------ NAV ------------------
const NAV_LINKS = [
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "experience", label: "Experience" },
  { id: "videos", label: "Videos" },
  { id: "testimonials", label: "Reviews" },
  { id: "locations", label: "Locations" },
  { id: "appointment", label: "Book" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-nav" data-testid="site-nav">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-2 group"
          data-testid="nav-logo-btn"
        >
          <div className="w-9 h-9 rounded-xl bg-emerald-600 grid place-items-center shadow-emerald-soft">
            <span className="text-white font-bold text-lg leading-none">+</span>
          </div>
          <div className="text-left leading-tight">
            <div className="font-heading font-semibold text-slate-900 text-[15px]">Dr. Anuj Singh</div>
            <div className="text-[11px] text-emerald-700 tracking-wider uppercase">Surgical Oncology</div>
          </div>
        </button>
        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="text-[14px] text-slate-600 hover:text-emerald-700 transition font-medium"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <a
            href={`tel:${PROFILE.phone}`}
            className="text-[13px] text-slate-700 hover:text-emerald-700 flex items-center gap-1.5 font-medium"
            data-testid="nav-call-link"
          >
            <Phone className="w-4 h-4" /> {PROFILE.phone_display}
          </a>
          <Button
            onClick={() => scrollTo("appointment")}
            className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-5"
            data-testid="nav-book-btn"
          >
            Book Appointment
          </Button>
        </div>
        <button
          className="lg:hidden p-2 text-slate-800"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className="text-left py-2.5 text-slate-700 font-medium"
                data-testid={`nav-mobile-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <Button
              onClick={() => scrollTo("appointment")}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-full mt-3"
              data-testid="nav-mobile-book-btn"
            >
              Book Appointment
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

// ------------------ HERO ------------------
const Hero = () => (
  <section id="hero" className="bg-hero-gradient pt-24 pb-12 sm:pt-28 sm:pb-16 lg:pt-36 lg:pb-24 relative overflow-hidden" data-testid="section-hero">
    <div className="bg-medical-grid absolute inset-0 opacity-50 pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center relative">
      <div className="reveal">
        <div className="inline-flex items-center gap-2 bg-emerald-600/10 text-emerald-700 px-3.5 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold tracking-wide uppercase mb-5 sm:mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
          Now consulting at SDMH Hospital, Jaipur
        </div>
        <h1 className="font-heading text-[2rem] leading-[1.1] sm:text-5xl lg:text-6xl font-semibold text-slate-900 mb-5 sm:mb-6">
          Advanced Cancer Care,
          <span className="block text-emerald-700">delivered with Compassion.</span>
        </h1>
        <p className="text-[15px] sm:text-base lg:text-lg text-slate-600 leading-relaxed max-w-xl mb-7 sm:mb-8">
          I'm <span className="font-semibold text-slate-900">{PROFILE.name}</span>, Surgical Oncologist trained at <span className="text-sky-700 font-semibold">Tata Memorial Hospital, Mumbai</span>. Specialised in minimally invasive cancer surgery and peritoneal surface malignancies.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => document.getElementById("appointment")?.scrollIntoView({ behavior: "smooth" })}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-7 h-12 shadow-emerald-soft font-medium"
            data-testid="hero-book-btn"
          >
            Book Appointment <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <a href={`tel:${PROFILE.phone}`} data-testid="hero-call-btn">
            <Button
              variant="outline"
              className="border-2 border-slate-300 text-slate-800 hover:bg-slate-50 rounded-full px-7 h-12 font-medium"
            >
              <Phone className="w-4 h-4 mr-2" /> Call Now
            </Button>
          </a>
          <a
            href={`https://wa.me/${PROFILE.whatsapp.replace(/\D/g, "")}`}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-btn"
          >
            <Button
              variant="outline"
              className="border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-full px-7 h-12 font-medium"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
          </a>
        </div>
        <div className="grid grid-cols-3 gap-3 sm:gap-6 mt-8 sm:mt-10 max-w-md">
          {[
            { v: PROFILE.experience_years, l: "Years Experience" },
            { v: PROFILE.surgeries, l: "Surgeries Performed" },
            { v: "Tata", l: "Memorial Trained" },
          ].map((s) => (
            <div key={s.l} data-testid={`hero-stat-${s.l.split(' ')[0].toLowerCase()}`}>
              <div className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{s.v}</div>
              <div className="text-[11px] sm:text-xs text-slate-500 mt-1 leading-tight">{s.l}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-8" data-testid="hero-socials">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Follow</span>
          <div className="h-px flex-1 max-w-[40px] bg-slate-200" />
          {SOCIAL_LINKS.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noreferrer"
              aria-label={s.label}
              className={`w-9 h-9 rounded-full bg-white border border-slate-200 grid place-items-center text-slate-600 ${s.color} hover:border-slate-300 hover:-translate-y-0.5 transition shadow-sm`}
              data-testid={`hero-social-${s.label.toLowerCase()}`}
            >
              <s.Icon className="w-4 h-4" />
            </a>
          ))}
        </div>
      </div>
      <div className="relative reveal flex flex-col items-center lg:items-end">
        <div className="relative w-[280px] h-[360px] sm:w-[380px] sm:h-[500px] lg:w-[460px] lg:h-[580px]">
          <div className="hero-blob absolute -inset-6 rounded-[50%]" />
          <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-emerald-600 via-emerald-500 to-sky-600 shadow-2xl" />
          <div className="absolute inset-2 rounded-[1.7rem] bg-gradient-to-br from-emerald-100 via-sky-50 to-emerald-50 overflow-hidden" />
          <img
            src={PROFILE.photo}
            alt={PROFILE.name}
            className="relative w-full h-full object-contain object-bottom drop-shadow-xl z-10"
            data-testid="hero-doctor-photo"
          />
          <div className="hidden lg:flex absolute -left-10 top-10 z-20 bg-white rounded-2xl shadow-2xl px-4 py-3 border border-slate-100 items-center gap-3" data-testid="hero-badge-credentials">
            <div className="w-11 h-11 rounded-xl bg-emerald-100 grid place-items-center text-emerald-700 shrink-0">
              <Award className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trained at</div>
              <div className="text-sm font-bold text-slate-900 leading-tight">Tata Memorial</div>
              <div className="text-[11px] text-emerald-700 font-medium">MCh Surgical Oncology</div>
            </div>
          </div>
          <div className="hidden lg:flex absolute -right-10 bottom-16 z-20 bg-white rounded-2xl shadow-2xl px-4 py-3 border border-slate-100 items-center gap-3" data-testid="hero-badge-availability">
            <div className="w-11 h-11 rounded-xl bg-sky-100 grid place-items-center text-sky-700 shrink-0">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Available</div>
              <div className="text-sm font-bold text-slate-900 leading-tight">Mon – Sat</div>
              <div className="text-[11px] text-sky-700 font-medium">9 AM – 8 PM</div>
            </div>
          </div>
        </div>
        <div className="lg:hidden flex flex-wrap justify-center gap-3 mt-6 w-full max-w-[420px]" data-testid="hero-mobile-trust">
          <div className="flex-1 min-w-[140px] flex items-center gap-2.5 px-3 py-2.5 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
              <Award className="w-4 h-4" />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trained at</div>
              <div className="text-[13px] font-bold text-slate-900">Tata Memorial</div>
            </div>
          </div>
          <div className="flex-1 min-w-[140px] flex items-center gap-2.5 px-3 py-2.5 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-sky-100 text-sky-700 grid place-items-center shrink-0">
              <Clock className="w-4 h-4" />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Available</div>
              <div className="text-[13px] font-bold text-slate-900">Mon – Sat · 9–8</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ------------------ ABOUT ------------------
const About = () => (
  <section id="about" className="py-14 sm:py-20 md:py-28 bg-white" data-testid="section-about">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
      <div className="reveal">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">About</div>
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 mb-6 leading-tight">
          A decade of cancer surgery, learnt at India's best institutions.
        </h2>
        <p className="text-slate-600 leading-relaxed mb-5">
          Dr. Anuj Singh is a fellowship-trained Surgical Oncologist with over a decade of dedicated experience in cancer care. He completed his MCh Surgical Oncology from Tata Memorial Hospital, Mumbai — India's most respected cancer institute — and has since served as faculty at Tata Memorial, MPMMCC Varanasi, and as Associate Professor & Unit Head at NIMS Jaipur. He currently practises as <span className="font-semibold text-slate-900">Consultant Surgical Oncologist at SDMH Hospital, Jaipur</span>.
        </p>
        <p className="text-slate-600 leading-relaxed mb-8">
          His practice is built on three pillars: evidence-based oncology, minimally invasive technique, and a multidisciplinary team approach so every patient receives a treatment plan that is right for them.
        </p>
        <div className="grid grid-cols-2 gap-4">
          {[
            { icon: GraduationCap, t: "MCh Surgical Oncology", s: "Tata Memorial, Mumbai" },
            { icon: Award, t: "MS General Surgery", s: "Dr. S.N. Medical College" },
            { icon: Microscope, t: "VATS Trained", s: "Shanghai, China" },
            { icon: Stethoscope, t: "Consultant Oncologist", s: "SDMH Hospital, Jaipur" },
          ].map((b) => (
            <div key={b.t} className="flex items-start gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <div className="w-10 h-10 rounded-xl bg-white border border-emerald-100 grid place-items-center text-emerald-700 shrink-0">
                <b.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">{b.t}</div>
                <div className="text-xs text-slate-500 mt-0.5">{b.s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="reveal lg:sticky lg:top-24">
        <div className="rounded-3xl overflow-hidden border border-slate-100 shadow-xl">
          <img
            src="https://images.unsplash.com/photo-1638202993928-7267aad84c31?w=1100&auto=format&fit=crop&q=75"
            alt="Doctor consulting patient"
            className="w-full h-[420px] object-cover"
          />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="rounded-2xl p-5 bg-emerald-600 text-white">
            <div className="font-heading text-3xl font-semibold">{PROFILE.experience_years}</div>
            <div className="text-emerald-100 text-sm mt-1">Years of dedicated cancer surgery practice</div>
          </div>
          <div className="rounded-2xl p-5 bg-sky-900 text-white">
            <div className="font-heading text-3xl font-semibold">{PROFILE.surgeries}</div>
            <div className="text-sky-200 text-sm mt-1">Cancer surgeries performed across major sites</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ------------------ SERVICES ------------------
const Services = () => (
  <section id="services" className="py-14 sm:py-20 md:py-28 bg-slate-50" data-testid="section-services">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-2xl mb-12 reveal">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Services</div>
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">
          Comprehensive cancer care, every step of the way.
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-5 auto-rows-fr">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon] || Stethoscope;
          const isFeature = i === 0;
          return (
            <div
              key={s.title}
              className={`reveal group relative bg-white border border-slate-100 rounded-3xl p-6 md:p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden ${s.span} ${isFeature ? "min-h-[260px] sm:min-h-[300px]" : ""}`}
              data-testid={`service-card-${i}`}
            >
              {isFeature && (
                <div className="absolute right-0 bottom-0 w-44 h-44 rounded-full bg-gradient-to-tr from-emerald-100 to-sky-100 -mr-12 -mb-12 opacity-70 group-hover:scale-110 transition-transform" />
              )}
              <div className="relative">
                <div className={`w-12 h-12 rounded-2xl grid place-items-center mb-5 ${s.accent === "emerald" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-lg md:text-xl font-semibold text-slate-900 mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                {isFeature && (
                  <div className="flex items-center gap-2 mt-6 text-sm font-medium text-emerald-700">
                    Smaller scars · Faster recovery · Same outcomes
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// ------------------ SIGNATURE EXPERTISE ------------------
const Expertise = () => (
  <section className="py-14 sm:py-20 md:py-28 bg-white relative overflow-hidden" data-testid="section-expertise">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-2xl mb-12 reveal">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Signature Expertise</div>
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">
          Two pillars of advanced cancer surgery.
        </h2>
        <p className="text-slate-600 mt-4 leading-relaxed">
          A focused practice built around techniques that meaningfully change patient outcomes — internationally trained, evidence-driven and patient-first.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* MIS Card */}
        <div className="reveal relative rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-700 via-emerald-800 to-sky-900 text-white p-6 sm:p-8 md:p-10" data-testid="expertise-mis">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-emerald-400/20 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[11px] font-semibold uppercase tracking-wider">
              <Microscope className="w-3.5 h-3.5" /> Minimally Invasive Oncosurgery
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold mt-5 leading-tight">
              Robotic. Laparoscopic. Uniportal VATS.
            </h3>
            <p className="text-emerald-50/90 mt-4 leading-relaxed text-[15px]">
              Cancer surgery through tiny keyhole incisions — and for the chest, often through a single port. Patients walk earlier, leave hospital faster, and resume normal life sooner, without compromising oncological outcomes.
            </p>
            <div className="grid grid-cols-3 gap-3 mt-7">
              {[
                { l: "Robotic", s: "Da Vinci · SSI" },
                { l: "Laparoscopic", s: "Keyhole" },
                { l: "Uniportal VATS", s: "Single port" },
              ].map((b) => (
                <div key={b.l} className="rounded-2xl bg-white/10 border border-white/15 p-4">
                  <div className="text-sm font-semibold">{b.l}</div>
                  <div className="text-[11px] text-emerald-100/80 mt-0.5">{b.s}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-7 p-4 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-10 h-10 rounded-xl bg-amber-400 text-amber-900 grid place-items-center shrink-0">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-amber-200 font-semibold">Trained at</div>
                <div className="text-sm font-semibold">Shanghai Pulmonary Hospital · Tongji University, China</div>
                <div className="text-[12px] text-emerald-100/80">International Uniportal VATS Training Program</div>
              </div>
            </div>
          </div>
        </div>

        {/* Peritoneal Card */}
        <div className="reveal relative rounded-3xl overflow-hidden bg-gradient-to-br from-sky-700 via-sky-800 to-emerald-900 text-white p-6 sm:p-8 md:p-10" data-testid="expertise-peritoneal">
          <div className="absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-sky-400/20 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[11px] font-semibold uppercase tracking-wider">
              <Activity className="w-3.5 h-3.5" /> Peritoneal Surface Malignancies
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold mt-5 leading-tight">
              CRS with HIPEC & PIPAC.
            </h3>
            <p className="text-sky-50/90 mt-4 leading-relaxed text-[15px]">
              Specialised treatment for cancers that have spread to the lining of the abdomen — combining cytoreductive surgery with heated intraperitoneal chemotherapy (HIPEC) and pressurised intraperitoneal aerosol chemotherapy (PIPAC). A second chance for patients once considered inoperable.
            </p>
            <div className="space-y-3 mt-7">
              {[
                { t: "HIPEC", d: "Heated Intraperitoneal Chemotherapy delivered intra-operatively after complete tumour removal." },
                { t: "PIPAC", d: "Pressurised Intraperitoneal Aerosol Chemotherapy — a minimally invasive option for advanced disease." },
                { t: "CRS", d: "Cytoreductive Surgery to remove all visible disease before chemotherapy delivery." },
              ].map((b) => (
                <div key={b.t} className="flex items-start gap-3 p-4 rounded-2xl bg-white/10 border border-white/15">
                  <div className="px-2.5 py-1 rounded-md bg-white text-sky-800 text-[11px] font-bold shrink-0">{b.t}</div>
                  <div className="text-[13px] text-sky-50/90 leading-relaxed">{b.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ------------------ EXPERIENCE / EDUCATION ------------------
const Experience = () => (
  <section id="experience" className="py-14 sm:py-20 md:py-28 bg-white" data-testid="section-experience">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-2xl mb-14 reveal">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Experience</div>
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">
          Trained at the best, serving patients close to home.
        </h2>
      </div>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div className="reveal">
          <div className="flex items-center gap-3 mb-7">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center">
              <Stethoscope className="w-5 h-5" />
            </div>
            <h3 className="font-heading text-2xl font-semibold text-slate-900">Work Experience</h3>
          </div>
          <div className="relative pl-7 border-l-2 border-emerald-100 space-y-7">
            {TIMELINE.map((t, i) => (
              <div key={i} className="relative" data-testid={`timeline-work-${i}`}>
                <div className="absolute -left-[34px] top-1 w-4 h-4 rounded-full bg-white border-2 border-emerald-600" />
                <div className="text-xs font-semibold text-emerald-700">{t.year}</div>
                <div className="font-heading text-lg font-semibold text-slate-900 mt-1">{t.role}</div>
                <div className="text-sm text-slate-600 mt-0.5 leading-relaxed">{t.place}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="reveal">
          <div className="flex items-center gap-3 mb-7">
            <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 grid place-items-center">
              <GraduationCap className="w-5 h-5" />
            </div>
            <h3 className="font-heading text-2xl font-semibold text-slate-900">Education & Training</h3>
          </div>
          <div className="relative pl-7 border-l-2 border-sky-100 space-y-7">
            {EDUCATION.map((t, i) => (
              <div key={i} className="relative" data-testid={`timeline-edu-${i}`}>
                <div className="absolute -left-[34px] top-1 w-4 h-4 rounded-full bg-white border-2 border-sky-600" />
                <div className="text-xs font-semibold text-sky-700">{t.year}</div>
                <div className="font-heading text-lg font-semibold text-slate-900 mt-1">{t.degree}</div>
                <div className="text-sm text-slate-600 mt-0.5 leading-relaxed">{t.place}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-sky-50 to-emerald-50 border border-slate-100">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-3">
              <Award className="w-4 h-4 text-emerald-700" /> Memberships
            </div>
            <ul className="space-y-2">
              {MEMBERSHIPS.map((m) => (
                <li key={m} className="text-sm text-slate-700 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  {m}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ------------------ PUBLICATIONS ------------------
const Publications = () => (
  <section className="py-12 sm:py-16 md:py-20 bg-slate-50" data-testid="section-publications">
    <div className="max-w-5xl mx-auto px-6 lg:px-12">
      <div className="reveal mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Research</div>
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
          Selected publications.
        </h2>
      </div>
      <div className="space-y-3">
        {PUBLICATIONS.map((p, i) => (
          <div
            key={i}
            className="reveal flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-sm transition"
            data-testid={`publication-${i}`}
          >
            <div className="w-10 h-10 shrink-0 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-slate-900 leading-snug">{p.title}</div>
              <div className="text-sm text-slate-500 mt-1">
                <span className="italic">{p.journal}</span> · {p.year}{p.detail ? ` · ${p.detail}` : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ------------------ TESTIMONIALS ------------------
const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const next = () => setIdx((i) => (i + 1) % TESTIMONIALS.length);
  const prev = () => setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, []);
  const t = TESTIMONIALS[idx];
  return (
    <section id="testimonials" className="py-14 sm:py-20 md:py-28 bg-gradient-to-br from-emerald-700 via-emerald-800 to-sky-900 text-white relative overflow-hidden" data-testid="section-testimonials">
      <div className="absolute inset-0 opacity-10 bg-medical-grid" />
      <div className="max-w-5xl mx-auto px-6 lg:px-12 relative">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-200 mb-3 reveal">Testimonials</div>
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-12 reveal">
          What patients say.
        </h2>
        <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-3xl p-6 sm:p-8 md:p-12 reveal" data-testid="testimonial-card">
          <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-300 mb-4 sm:mb-5" />
          <p className="text-lg sm:text-xl md:text-2xl font-heading font-light leading-relaxed mb-6 sm:mb-8">
            "{t.quote}"
          </p>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="font-semibold text-white">{t.name}</div>
              <div className="text-sm text-emerald-200">{t.relation} · {t.location}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-300 text-amber-300" />
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={prev} className="w-10 h-10 rounded-full border border-white/25 hover:bg-white/10 grid place-items-center" data-testid="testimonial-prev"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={next} className="w-10 h-10 rounded-full border border-white/25 hover:bg-white/10 grid place-items-center" data-testid="testimonial-next"><ChevronRight className="w-5 h-5" /></button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-center gap-2 mt-6">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-white" : "w-1.5 bg-white/40"}`}
              data-testid={`testimonial-dot-${i}`}
              aria-label={`Show testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// ------------------ PRESS / IN THE NEWS ------------------
const Press = () => (
  <section id="press" className="py-14 sm:py-20 md:py-24 bg-slate-50" data-testid="section-press">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-2xl mb-10 reveal">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">In the News</div>
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">
          Featured in the press.
        </h2>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {PRESS.map((p, i) => (
          <article
            key={i}
            className="reveal bg-white rounded-3xl border border-slate-100 overflow-hidden flex flex-col sm:grid sm:grid-cols-5 hover:shadow-xl transition-shadow"
            data-testid={`press-card-${i}`}
          >
            <div className="sm:col-span-2 bg-slate-50 overflow-hidden">
              <img src={p.src} alt={p.publication} className="w-full h-48 sm:h-full object-cover" />
            </div>
            <div className="sm:col-span-3 p-5 sm:p-6 md:p-7 flex flex-col">
              <div className="flex items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full font-semibold">
                  <Newspaper className="w-3 h-3" /> {p.publication}
                </span>
                <span className="text-slate-500">{p.date}</span>
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-slate-900 mt-4 leading-snug">{p.headline}</h3>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed flex-1">{p.summary}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

// ------------------ GALLERY ------------------
const Gallery = () => (
  <section id="gallery" className="py-14 sm:py-20 md:py-28 bg-white" data-testid="section-gallery">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-10 reveal">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Gallery</div>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">Inside the practice.</h2>
        </div>
        <div className="text-sm text-slate-500 max-w-sm">Glimpses from surgeries, conferences, and the team that makes our cancer care possible.</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {GALLERY.map((g, i) => (
          <div
            key={i}
            className={`reveal group relative overflow-hidden rounded-3xl bg-slate-100 ${i === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
            data-testid={`gallery-item-${i}`}
          >
            <img
              src={g.src}
              alt={g.caption || `Gallery ${i + 1}`}
              className={`w-full object-cover group-hover:scale-105 transition duration-700 ${i === 0 ? "h-[300px] md:h-[520px]" : "h-[220px]"}`}
            />
            {g.badge && (
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-amber-400 text-amber-950 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase shadow-md">
                <Award className="w-3 h-3" /> {g.badge}
              </div>
            )}
            {g.caption && (
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="text-white text-sm font-medium">{g.caption}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ------------------ VIDEOS ------------------
const Videos = () => {
  const items = VIDEOS;
  return (
    <section id="videos" className="py-14 sm:py-20 md:py-28 bg-slate-950 text-white relative overflow-hidden" data-testid="section-videos">
      <div className="absolute inset-0 opacity-[0.07] bg-medical-grid" />
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-red-600/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 relative">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12 reveal">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3">Videos</div>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight">
              Watch & learn —<span className="block text-emerald-400">cancer awareness, simplified.</span>
            </h2>
          </div>
          <div className="flex gap-2">
            <a
              href={PROFILE.socials.youtube}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 px-4 py-3 rounded-full bg-red-600 hover:bg-red-700 text-white transition shadow-lg"
              data-testid="videos-subscribe-btn"
            >
              <Youtube className="w-5 h-5" />
              <span className="font-semibold text-sm hidden sm:inline">YouTube</span>
            </a>
            <a
              href={PROFILE.socials.instagram}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-tr from-fuchsia-600 via-pink-500 to-orange-400 hover:opacity-90 text-white transition shadow-lg"
              data-testid="videos-instagram-btn"
            >
              <Instagram className="w-5 h-5" />
              <span className="font-semibold text-sm hidden sm:inline">Instagram</span>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
          {items.map((v, i) => {
            const isInsta = v.type === "instagram";
            const src = isInsta
              ? `https://www.instagram.com/reel/${v.id}/embed`
              : `https://www.youtube.com/embed/${v.id}?rel=0`;
            const iconColor = isInsta ? "text-pink-400" : "text-emerald-400";
            const IconEl = isInsta ? Instagram : PlayCircle;
            return (
              <div key={v.id} className={`reveal ${items.length % 2 !== 0 && i === items.length - 1 ? "col-span-2 sm:col-span-2 lg:col-span-1" : ""}`} data-testid={`video-item-${i}`}>
                <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10 bg-black aspect-[9/16] shadow-2xl mx-auto max-w-[260px] sm:max-w-none">
                  <iframe
                    src={src}
                    title={v.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    scrolling="no"
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs sm:text-sm text-slate-300 justify-center sm:justify-start">
                  <IconEl className={`w-4 h-4 ${iconColor}`} />
                  <span>{v.title}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ------------------ BLOG ------------------
const Blog = () => (
  <section id="blog" className="py-14 sm:py-20 md:py-28 bg-slate-50" data-testid="section-blog">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-12 reveal">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Health Articles</div>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">From the journal.</h2>
        </div>
        <a
          href={PROFILE.socials.youtube}
          target="_blank"
          rel="noreferrer"
          className="group inline-flex items-center gap-3 px-5 py-3 rounded-full bg-white border border-slate-200 hover:border-red-200 hover:bg-red-50 transition"
          data-testid="blog-youtube-cta"
        >
          <span className="w-9 h-9 rounded-full bg-red-600 grid place-items-center text-white">
            <Youtube className="w-5 h-5" />
          </span>
          <span>
            <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Watch on YouTube</span>
            <span className="block text-sm font-medium text-slate-900 group-hover:text-red-700">@advancecancercarejaipur</span>
          </span>
          <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-red-600" />
        </a>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {BLOG.map((b, i) => (
          <article
            key={i}
            className="reveal group bg-white rounded-3xl overflow-hidden border border-slate-100 hover:shadow-xl transition-all duration-300"
            data-testid={`blog-card-${i}`}
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                <span className="bg-emerald-100 text-emerald-700 px-2.5 py-0.5 rounded-full font-medium">{b.tag}</span>
                <span>·</span>
                <span>{b.date}</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-900 leading-snug mb-2 group-hover:text-emerald-700 transition">{b.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{b.excerpt}</p>
              <div className="text-sm font-medium text-emerald-700 inline-flex items-center gap-1">
                Read more <ArrowUpRight className="w-4 h-4" />
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

// ------------------ LOCATIONS ------------------
const Locations = () => (
  <section id="locations" className="py-14 sm:py-20 md:py-28 bg-white" data-testid="section-locations">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-2xl mb-12 reveal">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Visit</div>
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">Two locations across Jaipur.</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {CLINICS.map((c) => (
          <div key={c.id} className="reveal rounded-3xl overflow-hidden border border-slate-100 bg-white" data-testid={`clinic-${c.id}`}>
            <iframe
              title={c.name}
              src={c.map}
              className="w-full h-56 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="p-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-1">{c.short}</div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 leading-snug">{c.name}</h3>
                </div>
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-5 space-y-2.5 text-sm">
                <div className="flex items-start gap-2.5 text-slate-700"><MapPin className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" /> {c.address}</div>
                <div className="flex items-start gap-2.5 text-slate-700"><Clock className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" /> {c.timings}</div>
                <div className="flex items-start gap-2.5 text-slate-700"><Calendar className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" /> {c.days}</div>
              </div>
              <div className="flex gap-3 mt-6">
                <a href={c.direct} target="_blank" rel="noreferrer" data-testid={`clinic-${c.id}-directions`}>
                  <Button variant="outline" className="rounded-full border-2 border-slate-200 hover:bg-slate-50">Get Directions <ArrowUpRight className="w-4 h-4 ml-1" /></Button>
                </a>
                <a href={`tel:${PROFILE.phone}`} data-testid={`clinic-${c.id}-call`}>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full"><Phone className="w-4 h-4 mr-2" /> Call</Button>
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ------------------ APPOINTMENT ------------------
const Appointment = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    clinic: "SDMH",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.clinic) {
      toast.error("Please fill name, phone and clinic");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...form,
        email: form.email || undefined,
      });
      toast.success("Appointment requested! Our team will call you shortly.");
      setForm({ name: "", phone: "", email: "", clinic: "SDMH", preferred_date: "", preferred_time: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please call " + PROFILE.phone_display);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="appointment" className="py-14 sm:py-20 md:py-28 bg-gradient-to-br from-slate-50 to-emerald-50/40" data-testid="section-appointment">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-2 reveal lg:sticky lg:top-24">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Book an appointment</div>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold text-slate-900 leading-tight mb-5">
            Request a consultation. We'll call you back within hours.
          </h2>
          <p className="text-slate-600 leading-relaxed mb-7">
            Share a few details and our team will confirm your slot at SDMH or Vaani Clinic. For urgent matters, please call directly.
          </p>
          <div className="space-y-3">
            <a href={`tel:${PROFILE.phone}`} className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-100 hover:border-emerald-200 transition" data-testid="appt-call-row">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><Phone className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-slate-500">Call</div>
                <div className="font-medium text-slate-900">{PROFILE.phone_display}</div>
              </div>
            </a>
            <a href={`https://wa.me/${PROFILE.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-100 hover:border-emerald-200 transition" data-testid="appt-whatsapp-row">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><MessageCircle className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-slate-500">WhatsApp</div>
                <div className="font-medium text-slate-900">{PROFILE.whatsapp_display}</div>
              </div>
            </a>
            <a href={`mailto:${PROFILE.email}`} className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-100 hover:border-emerald-200 transition" data-testid="appt-email-row">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><Mail className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-slate-500">Email</div>
                <div className="font-medium text-slate-900">{PROFILE.email}</div>
              </div>
            </a>
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-3xl p-6 sm:p-8 md:p-10 border border-slate-100 shadow-sm reveal" data-testid="appointment-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="appt-name" className="text-slate-700 mb-2 block">Full Name *</Label>
              <Input id="appt-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="h-12 rounded-xl" data-testid="appt-input-name" />
            </div>
            <div>
              <Label htmlFor="appt-phone" className="text-slate-700 mb-2 block">Phone *</Label>
              <Input id="appt-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91" className="h-12 rounded-xl" data-testid="appt-input-phone" />
            </div>
            <div>
              <Label htmlFor="appt-email" className="text-slate-700 mb-2 block">Email</Label>
              <Input id="appt-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="h-12 rounded-xl" data-testid="appt-input-email" />
            </div>
            <div>
              <Label className="text-slate-700 mb-2 block">Preferred Clinic *</Label>
              <Select value={form.clinic} onValueChange={(v) => setForm({ ...form, clinic: v })}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="appt-select-clinic"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SDMH">SDMH Hospital (9 AM – 4 PM)</SelectItem>
                  <SelectItem value="Vaani">Vaani Clinic (6 PM – 8 PM)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="appt-date" className="text-slate-700 mb-2 block">Preferred Date</Label>
              <Input id="appt-date" type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="h-12 rounded-xl" data-testid="appt-input-date" />
            </div>
            <div>
              <Label htmlFor="appt-time" className="text-slate-700 mb-2 block">Preferred Time</Label>
              <Input id="appt-time" type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="h-12 rounded-xl" data-testid="appt-input-time" />
            </div>
          </div>
          <div className="mt-5">
            <Label htmlFor="appt-msg" className="text-slate-700 mb-2 block">Briefly describe your concern</Label>
            <Textarea id="appt-msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Symptoms, prior reports, or anything we should know..." rows={4} className="rounded-xl" data-testid="appt-input-message" />
          </div>
          <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 rounded-full mt-6 h-12 px-8 shadow-emerald-soft font-medium" data-testid="appt-submit-btn">
            {loading ? "Submitting..." : "Request Appointment"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
          <div className="text-xs text-slate-500 mt-4 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
            Your information is confidential. We never share patient details with third parties.
          </div>
        </form>
      </div>
    </section>
  );
};

// ------------------ FOOTER ------------------
const Footer = () => (
  <footer className="bg-slate-950 text-slate-300 pt-16 pb-8" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-4 gap-10">
      <div className="md:col-span-2">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-xl bg-emerald-600 grid place-items-center"><span className="text-white font-bold text-lg leading-none">+</span></div>
          <div>
            <div className="font-heading font-semibold text-white text-base">Dr. Anuj Singh</div>
            <div className="text-xs text-emerald-400 tracking-wider uppercase">Surgical Oncology</div>
          </div>
        </div>
        <p className="text-sm text-slate-400 max-w-md leading-relaxed">
          Consultant Surgical Oncologist trained at Tata Memorial Hospital, Mumbai. Specialised in minimally invasive cancer surgery and peritoneal surface malignancies.
        </p>
        <div className="flex gap-3 mt-6">
          <a href={`tel:${PROFILE.phone}`} className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm flex items-center gap-2"><Phone className="w-4 h-4" /> Call</a>
          <a href={`https://wa.me/${PROFILE.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm flex items-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
        </div>
        <div className="flex gap-2 mt-5" data-testid="footer-socials">
          {SOCIAL_LINKS.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noreferrer"
              aria-label={s.label}
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 grid place-items-center text-slate-300 hover:text-white transition"
              data-testid={`footer-social-${s.label.toLowerCase()}`}
            >
              <s.Icon className="w-4 h-4" />
            </a>
          ))}
        </div>
      </div>
      <div>
        <div className="font-heading font-semibold text-white text-sm mb-4">Quick Links</div>
        <ul className="space-y-2.5 text-sm">
          {NAV_LINKS.map((l) => (
            <li key={l.id}>
              <button onClick={() => document.getElementById(l.id)?.scrollIntoView({ behavior: "smooth" })} className="hover:text-emerald-400 transition" data-testid={`footer-link-${l.id}`}>{l.label}</button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="font-heading font-semibold text-white text-sm mb-4">Contact</div>
        <ul className="space-y-2.5 text-sm text-slate-400">
          <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />{PROFILE.phone_display}</li>
          <li className="flex items-start gap-2"><MessageCircle className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />{PROFILE.whatsapp_display}</li>
          <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />{PROFILE.email}</li>
          <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />Jaipur, Rajasthan, India</li>
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-10 mt-10 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 text-xs text-slate-500">
      <div>© {new Date().getFullYear()} Dr. Anuj Singh. All rights reserved.</div>
      <div>For medical emergencies, please visit the nearest hospital or dial 112.</div>
    </div>
  </footer>
);

// ------------------ FLOATING CTA ------------------
const FloatingCTA = () => (
  <>
    <a
      href={`https://wa.me/${PROFILE.whatsapp.replace(/\D/g, "")}?text=Hello%20Dr.%20Anuj%20Singh%2C%20I%27d%20like%20to%20book%20a%20consultation.`}
      target="_blank"
      rel="noreferrer"
      className="fixed bottom-6 right-6 z-40 bg-[#25D366] text-white p-4 rounded-full shadow-xl hover:scale-110 transition-transform"
      data-testid="float-whatsapp-btn"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle className="w-6 h-6" />
    </a>
    <a
      href={`tel:${PROFILE.phone}`}
      className="fixed bottom-24 right-6 z-40 bg-emerald-600 text-white p-4 rounded-full shadow-xl hover:scale-110 transition-transform md:hidden"
      data-testid="float-call-btn"
      aria-label="Call now"
    >
      <Phone className="w-6 h-6" />
    </a>
  </>
);

// ------------------ HOME ------------------
const Home = () => {
  useReveal();
  return (
    <div className="App">
      <Nav />
      <main>
        <Hero />
        <About />
        <Services />
        <Expertise />
        <Experience />
        <Publications />
        <Testimonials />
        <Press />
        <Gallery />
        <Videos />
        <Blog />
        <Locations />
        <Appointment />
      </main>
      <Footer />
      <FloatingCTA />
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
